import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import { hasCapability } from "@/lib/brokerAuth";
import {
  listAccounts,
  type AccountSummary,
  type AccountsPage,
} from "@/lib/brokerAccounts";
import { messageFor } from "@/lib/errors";

// The brokerage's own accounts (BRK-S033). List shows summary only — never
// SSN/EIN — with search + an include-inactive toggle + paging; each row links to
// the detail. "Add account" is gated on account:edit (the server enforces the
// same). Reading requires account:view.
//
// MOUNTED TWICE, ONE COMPONENT (BRK-S138). The Surety platform lists the SAME
// own-scoped accounts — there is no surety-specific account — so the only thing
// that differs is where a row goes: `/accounts/{id}` is the Commercial card,
// `/surety/accounts/{id}` the bond profile. A second list page would have been a
// copy that drifts in search, paging and empty states, so the destination is a
// prop and the platform is decided by the ROUTE (BRK-S090), not in here.
const PAGE_SIZE = 20;

export function AccountsListPage({
  /** Where a row goes, and where "Back" returns to. `/accounts` by default —
   *  the Commercial mount, which is also what a deep link with no platform
   *  gets. */
  basePath = "/accounts",
}: {
  basePath?: string;
} = {}) {
  const { identity, authedRequest } = useAuth();
  const canView = hasCapability(identity, "account:view");
  const canEdit = hasCapability(identity, "account:edit");

  const [search, setSearch] = useState("");
  const [query, setQuery] = useState(""); // the applied (submitted) search
  const [includeInactive, setIncludeInactive] = useState(false);
  const [offset, setOffset] = useState(0);
  const [page, setPage] = useState<AccountsPage | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!canView) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const result = await listAccounts(authedRequest, {
          search: query || undefined,
          includeInactive,
          limit: PAGE_SIZE,
          offset,
        });
        if (!cancelled) setPage(result);
      } catch (err) {
        if (!cancelled) setError(messageFor(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authedRequest, canView, query, includeInactive, offset]);

  useEffect(() => load(), [load]);

  function onSearch(e: FormEvent) {
    e.preventDefault();
    setOffset(0);
    setQuery(search.trim());
  }

  // One derived flag rather than a string compare at four sites.
  const surety = basePath !== "/accounts";
  const total = page?.total ?? 0;
  const items = page?.items ?? [];
  const showingFrom = total === 0 ? 0 : offset + 1;
  const showingTo = Math.min(offset + PAGE_SIZE, total);

  return (
    <section className="mx-auto flex w-full max-w-3xl flex-col gap-6 py-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-bold tracking-tight">Accounts</h1>
          <p className="text-brk-dim">
            The insured accounts your brokerage works.
          </p>
        </div>
        {canEdit && (
          <Link
            to="/accounts/new"
            className="rounded-md bg-brk-accent px-4 py-2 font-medium text-white transition-colors hover:bg-brk-accent-strong"
          >
            Add account
          </Link>
        )}
      </div>

      {!canView && (
        <p role="alert" className="text-sm text-brk-danger">
          You don’t have access to view accounts.
        </p>
      )}

      {canView && (
        <>
          <form
            className="flex flex-col gap-3 sm:flex-row sm:items-center"
            onSubmit={onSearch}
          >
            <input
              type="search"
              aria-label="Search accounts"
              placeholder="Search by name, DBA, or account number"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full flex-1 rounded-md border border-brk-border bg-brk-panel px-3 py-2 text-brk-text outline-none focus:border-brk-accent focus:ring-2 focus:ring-brk-accent/30"
            />
            <button
              type="submit"
              className="rounded-md border border-brk-border px-4 py-2 font-medium text-brk-accent transition-colors hover:bg-brk-bg"
            >
              Search
            </button>
            <label className="flex items-center gap-2 text-sm text-brk-dim">
              <input
                type="checkbox"
                checked={includeInactive}
                onChange={(e) => {
                  setOffset(0);
                  setIncludeInactive(e.target.checked);
                }}
              />
              Include inactive
            </label>
          </form>

          {error && (
            <p role="alert" className="text-sm text-brk-danger">
              {error}
            </p>
          )}

          {loading && (
            <p role="status" className="text-sm text-brk-dim">
              Loading accounts…
            </p>
          )}

          {!loading && !error && items.length === 0 && (
            <p className="text-sm text-brk-dim">
              No accounts found.{" "}
              {canEdit && (
                <Link
                  to="/accounts/new"
                  className="text-brk-accent hover:underline"
                >
                  Add one
                </Link>
              )}
            </p>
          )}

          {items.length > 0 && (
            <ul className="flex flex-col gap-2">
              {items.map((account) => (
                <li key={account.id}>
                  <AccountRow account={account} basePath={basePath} />
                </li>
              ))}
            </ul>
          )}

          {total > 0 && (
            <div className="flex items-center justify-between text-sm text-brk-dim">
              <span>
                Showing {showingFrom}–{showingTo} of {total}
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={offset === 0}
                  onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                  className="rounded-md border border-brk-border px-3 py-1 text-brk-accent transition-colors hover:bg-brk-bg disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  type="button"
                  disabled={showingTo >= total}
                  onClick={() => setOffset(offset + PAGE_SIZE)}
                  className="rounded-md border border-brk-border px-3 py-1 text-brk-accent transition-colors hover:bg-brk-bg disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Back to the platform this mount belongs to: the Exchange for
          Commercial, the Construction exchange for Surety. */}
      <Link
        to={surety ? "/surety/construction" : "/portal"}
        className="text-sm text-brk-accent hover:underline"
      >
        ← Back to {surety ? "Construction Bond" : "portal"}
      </Link>
    </section>
  );
}

function AccountRow({
  account,
  basePath,
}: {
  account: AccountSummary;
  basePath: string;
}) {
  return (
    <Link
      to={`${basePath}/${account.id}`}
      className="flex items-center justify-between gap-3 rounded-lg border border-brk-border bg-brk-panel p-4 transition-colors hover:border-brk-accent"
    >
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="truncate font-medium text-brk-text">
          {account.legalName}
        </span>
        <span className="truncate text-sm text-brk-dim">
          {account.dbaName && account.dbaName !== account.legalName
            ? `${account.dbaName} · `
            : ""}
          {account.accountNumber}
        </span>
      </div>
    </Link>
  );
}
